import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import { formatDateTimeWithSeconds } from '@/lib/format-date';
import type { TroubleshootSession } from '../hooks/useTroubleshoot';

/** Status → badge color: running=green(default) · failed=red · ended=grey · creating/paused=outline. */
export function statusVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'running':
      return 'default';
    case 'failed':
      return 'destructive';
    case 'ended':
      return 'secondary';
    default:
      return 'outline';
  }
}

interface Props {
  sessions: TroubleshootSession[];
  onRowClick: (s: TroubleshootSession) => void;
  /** Show the Account column (the cross-tenant list); hidden in the per-device drawer. */
  showAccount?: boolean;
  emptyText?: string;
}

/** Shared read-only table of troubleshoot sessions — used by the console list page + the device drawer. */
export function SessionsTable({
  sessions,
  onRowClick,
  showAccount = true,
  emptyText = 'No troubleshooting sessions.',
}: Props) {
  const cols = showAccount ? 7 : 6;
  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{'Time'}</TableHead>
            {showAccount ? <TableHead>{'Account'}</TableHead> : null}
            <TableHead>{'Device'}</TableHead>
            <TableHead>{'Title'}</TableHead>
            <TableHead>{'Started by'}</TableHead>
            <TableHead>{'Mode'}</TableHead>
            <TableHead>{'Status'}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sessions.map((s) => (
            <TableRow
              key={s.sessionId}
              className="cursor-pointer"
              onClick={() => onRowClick(s)}
              data-testid="troubleshoot-row"
            >
              <TableCell className="whitespace-nowrap text-sm">
                {s.startedAt ? formatDateTimeWithSeconds(s.startedAt) : '—'}
              </TableCell>
              {showAccount ? (
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {s.accountId ?? '—'}
                </TableCell>
              ) : null}
              <TableCell>
                <div>{s.deviceName || <span className="text-muted-foreground">{'(unnamed)'}</span>}</div>
                <div className="font-mono text-xs text-muted-foreground">{s.deviceUUID}</div>
              </TableCell>
              <TableCell>{s.title || <span className="text-muted-foreground">{'—'}</span>}</TableCell>
              <TableCell className="text-sm">{s.actor}</TableCell>
              <TableCell>
                <Badge variant={s.mode === 'staff' ? 'default' : 'secondary'}>{s.mode}</Badge>
              </TableCell>
              <TableCell>
                <Badge variant={statusVariant(s.status)}>{s.status}</Badge>
              </TableCell>
            </TableRow>
          ))}
          {!sessions.length ? (
            <TableRow>
              <TableCell colSpan={cols} className="text-center text-sm text-muted-foreground">
                {emptyText}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  );
}
